'use client';

import { useState } from 'react';
import { Button } from '@myapp/ui/components/button';
import { Input } from '@myapp/ui/components/input';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Alert, AlertDescription } from '@myapp/ui/components/alert';
import { Badge } from '@myapp/ui/components/badge';
import { ToastProvider, useToast } from '@myapp/ui/components/toast';
import { PurchaseSuccessDialog } from './PurchaseSuccessDialog';
import { FormField, FormError } from './FormError';
import { 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  ShoppingCart, 
  Heart,
  MessageSquare,
  Bell,
  Zap
} from 'lucide-react';

function DemoControls() {
  const { toast } = useToast();
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
    phone: ''
  });

  const showSuccessToast = () => {
    toast({
      type: 'success',
      title: '성공!',
      description: '상품이 장바구니에 추가되었습니다.',
      duration: 4000
    });
  };

  const showErrorToast = () => {
    toast({
      type: 'error',
      title: '오류 발생',
      description: '네트워크 연결을 확인해 주세요.',
      duration: 5000
    });
  };

  const showInfoToast = () => {
    toast({
      type: 'info',
      title: '알림',
      description: '새로운 제철 특산물이 업데이트되었습니다.',
      duration: 3000
    });
  };

  const triggerFormErrors = () => {
    setFormErrors({
      email: '올바른 이메일 주소를 입력해주세요.',
      password: '비밀번호는 8자 이상이어야 합니다.',
      phone: '휴대폰 번호 형식이 올바르지 않습니다.'
    });
  };

  const clearFormErrors = () => {
    setFormErrors({ email: '', password: '', phone: '' });
  };

  return (
    <div className="space-y-8">
      {/* Toast Demo Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            토스트 알림 데모
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button onClick={showSuccessToast} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              성공 토스트
            </Button>
            <Button onClick={showErrorToast} variant="destructive">
              <AlertTriangle className="w-4 h-4 mr-2" />
              에러 토스트
            </Button>
            <Button onClick={showInfoToast} variant="outline">
              <Info className="w-4 h-4 mr-2" />
              정보 토스트
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Demo Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            구매 완료 다이얼로그 데모
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={() => setIsPurchaseDialogOpen(true)}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
          >
            구매 완료 팝업 보기
          </Button>
        </CardContent>
      </Card>

      {/* Form Error Demo Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            폼 에러 메시지 데모
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <Button onClick={triggerFormErrors} variant="destructive">
              에러 표시
            </Button>
            <Button onClick={clearFormErrors} variant="outline">
              에러 지우기
            </Button>
          </div>

          <div className="space-y-4">
            <FormField label="이메일" error={formErrors.email} required>
              <Input type="email" placeholder="이메일을 입력하세요" />
            </FormField>
            
            <FormField label="비밀번호" error={formErrors.password} required>
              <Input type="password" placeholder="비밀번호를 입력하세요" />
            </FormField>
            
            <FormField label="휴대폰 번호" error={formErrors.phone}>
              <Input type="tel" placeholder="휴대폰 번호를 입력하세요" />
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* Alert Demo Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            알림 메시지 데모
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>정보:</strong> 배송지 정보를 확인해 주세요.
            </AlertDescription>
          </Alert>

          <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>주의:</strong> 재고가 얼마 남지 않았습니다.
            </AlertDescription>
          </Alert>

          <Alert className="border-green-200 bg-green-50 text-green-800">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>성공:</strong> 주문이 성공적으로 처리되었습니다.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Status Badges Demo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            상태 뱃지 데모
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">대기중</Badge>
            <Badge className="bg-blue-100 text-blue-800 border-blue-200">처리중</Badge>
            <Badge className="bg-green-100 text-green-800 border-green-200">완료</Badge>
            <Badge variant="destructive">취소됨</Badge>
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">보류</Badge>
            <Badge className="bg-purple-100 text-purple-800 border-purple-200">프리미엄</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Success Dialog */}
      <PurchaseSuccessDialog 
        isOpen={isPurchaseDialogOpen}
        onClose={() => setIsPurchaseDialogOpen(false)}
      />
    </div>
  );
}

export function FeedbackDemoPanel() {
  return (
    <ToastProvider>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            피드백 UI 컴포넌트 데모
          </h1>
          <p className="text-gray-600">
            토스트, 알림, 다이얼로그 등 사용자 피드백 컴포넌트들을 확인해보세요.
          </p>
        </div>
        
        <DemoControls />
      </div>
    </ToastProvider>
  );
}